<template>
  <section
    v-if="active"
    class="octadesk-app-messenger"
    :class="{
      'kb-card-bottom-offset': kbEnabled && showKbCard
    }"
  >
    <Overlay />

    <WidgetHeader v-if="showWidgetHeader" />

    <KbCard v-if="kbOpen" @cta="handleKbCardCta" />

    <FooterPoweredBy v-if="kbOpen" />

    <ConversationHeader v-if="showConversationHeader" v-show="!kbOpen" />

    <div v-if="showLoader" v-show="!kbOpen" class="closeChatLoader">
      <ScaleLoader
        :color="'#5da9dd'"
        :height="'12px'"
        :width="'2px'"
      ></ScaleLoader>
    </div>

    <ConversationFooter v-show="!kbOpen"></ConversationFooter>

    <ConversationBody
      v-show="!kbOpen"
      :style="{
        height: conversationBodyHeight
      }"
    ></ConversationBody>
  </section>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex'

  import ScaleLoader from 'vue-spinner/src/ScaleLoader.vue'

  import WidgetHeader from '@/app/components/WidgetHeader'

  import ChatBeep from '@/assets/chat-beep.mp3'

  import { roomStatus } from '@/helpers'

  import ConversationHeader from './ConversationHeader.vue'

  import ConversationFooter from './ConversationFooter.vue'

  import ConversationBody from './ConversationBody.vue'

  import KbCard from '@/app/components/KbCard'

  import Overlay from '@/app/shared-components/Overlay'

  import FooterPoweredBy from '@/app/components/FooterPoweredBy'

  const beep = new Audio(ChatBeep)

  let verifyWaitingMessageTimeout

  export default {
    components: {
      ScaleLoader,
      WidgetHeader,
      KbCard,
      ConversationHeader,
      ConversationFooter,
      ConversationBody,
      Overlay,
      FooterPoweredBy
    },
    data() {
      return {
        beeping: false,
        chatReady: false
      }
    },
    computed: {
      ...mapGetters([
        'settings',
        'agent',
        'kbEnabled',
        'unrededMessages',
        'active',
        'chat',
        'chatStatus',
        'waitingRuleExecuted',
        'waitingMessageDelay',
        'hasWaitingMessage',
        'closingChatLoader',
        'headerHeight',
        'conversationFooterHeight',
        'restoringSession',
        'showKbCard',
        'inBotFlux',
        'botHasFinished'
      ]),
      conversationBodyHeight() {
        return this.headerHeight && this.conversationFooterHeight
          ? `calc(100% - ${
              this.headerHeight + this.conversationFooterHeight
            }px)`
          : undefined
      },
      kbOpen() {
        return this.kbEnabled && this.showKbCard
      },
      showWidgetHeader() {
        return !this.agent && !this.inBotFlux
      },
      showConversationHeader() {
        return this.agent || this.inBotFlux
      },
      showLoader() {
        return (
          this.agent &&
          this.closingChatLoader &&
          this.settings.conversation.clientCanClose
        )
      }
    },
    watch: {
      active(value) {
        if (value) {
          this.setOverlayOpen(false)
        }
      },
      chatStatus() {
        if (
          this.chatStatus === roomStatus.waiting ||
          (this.chatStatus === roomStatus.started && !this.waitingRuleExecuted)
        ) {
          this.verifyWaitingMessage()
        }
      },
      unrededMessages() {
        this.beeping = false
        if (
          this.unrededMessages > 0 &&
          (!this.active || !this.onFocus) &&
          !this.beeping
        ) {
          this.beep()
        } else if (this.unrededMessages === 0) {
          this.beeping = false
        }
      }
    },
    mounted() {
      if (!this.chat) {
        this.startConversation()
          .then(() => {
            if (
              this.kbEnabled &&
              this.chat &&
              this.chat.messages.length === 0
            ) {
              this.setShowKbCard(true)
            }

            this.chatReady = true
          })
          .catch(() => {
            this.chatReady = true
          })
      } else {
        if (this.kbEnabled && this.chat && this.chat.messages.length === 0) {
          this.setShowKbCard(true)
        }
        this.chatReady = true
      }
    },
    methods: {
      ...mapActions([
        'executeWaitingMessage',
        'createChat',
        'setShowKbCard',
        'setOverlayOpen'
      ]),
      beep() {
        beep.play()
      },
      verifyWaitingMessage() {
        if (this.hasWaitingMessage) {
          if (verifyWaitingMessageTimeout) {
            clearTimeout(verifyWaitingMessageTimeout)

            verifyWaitingMessageTimeout = undefined
          }
          verifyWaitingMessageTimeout = setTimeout(() => {
            if (!this.waitingRuleExecuted) {
              const kbEnabledCondition =
                this.kbEnabled && this.chatStatus === roomStatus.waiting

              const kbDisabledCondition =
                !this.kbEnabled &&
                (this.chatStatus === roomStatus.started ||
                  this.chatStatus === roomStatus.waiting)

              if (kbEnabledCondition || kbDisabledCondition) {
                this.executeWaitingMessage()
              }
            } else {
              clearTimeout(verifyWaitingMessageTimeout)
              verifyWaitingMessageTimeout = undefined
            }
          }, this.waitingMessageDelay)
        }
      },
      handleKbCardCta() {
        this.setShowKbCard(false)
      },
      async startConversation() {
        if (!this.restoringSession && !this.chat) {
          await this.createChat()
        }
      }
    }
  }
</script>

<style scoped>
  .octadesk-app-messenger {
    position: relative;
    background: linear-gradient(
      to bottom,
      rgba(255, 255, 255, 0) 0%,
      rgba(253, 253, 253, 1) 20px,
      rgba(246, 246, 246, 1) 100%
    );
    border-radius: 8px;
    height: 100%;
  }

  .octadesk-app-messenger-content-wrapper {
    background: #aa0000;
  }

  .chat--container--loader {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 1;
    width: 100%;
    background: #fff;
    padding-top: 50%;
  }

  .closeChatLoader {
    position: absolute;
    z-index: 1;
    width: 100%;
    background: rgb(238, 238, 238);
    height: calc(100% - 50px);
    padding-top: 70%;
  }

  .kb-card-bottom-offset {
    padding-bottom: 2.5rem;
  }
</style>
